import { Button, Col, Form, InputNumber, Modal, ModalProps, notification, Radio, Row, Select, Spin, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { queryClient } from "src";
import BinApiService from "src/api/BinApiService";
import GrainType from "src/consts/GrainType";
import LowGrainShutOffMode from "src/consts/LowGrainShutOffMode";
import SweepOffTime from "src/consts/SweepOffTime";
import DryingOperationsSettingsDTO from "src/models/DryingOperationsSettingsDTO";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import PremierPlusSyncSettingsRequestDTO from "src/models/PremierPlusSyncSettingsRequestDTO";
import { formatNumber } from "src/pages/dashboard/BinStatusPage/HeaterControls";
import { useGetPremierPlusBinDto } from "src/queries/useGetPremierPlusBinDto";
import { convertCelsiusToFahrenheit, convertCelsiusToFahrenheitOffset, convertFahrenheitToCelsius, convertFahrenheitToCelsiusOffset } from "src/utils/generalUtils";
import { useCelsiusContext } from "../useCelsiusContext";
import { getGrainTypeStr } from "src/utils/enumUtils";

const { Text, Title } = Typography;
const { Option } = Select;

interface PremierPlusDryerSettingsModalProps extends ModalProps {
    binDto: PremierPlusBinDTO | null;
    onClose: (result: any) => void;
}

export function getEnumOptions<T extends object>(enumObj: T): { label: string; value: number }[] {
    return Object.keys(enumObj)
        .filter(key => isNaN(Number(key))) // Exclude reverse mapping keys
        .map(key => ({
            label: key,
            value: (enumObj as any)[key] as number
        }));
}

function addToPossiblyMissingValue(original: number | null | undefined, increase: number): number | null | undefined
{
    if (original == null) {
        return original;
    }
    return original + increase;
}

function substractMaybe(a: number | null | undefined, b: number | null | undefined) {
    if (a == null) {    
        return a;
    }
    if (b == null) {
        return a;
    }
    return a - b;
}

const PremierPlusDryerSettingsModal: React.FC<PremierPlusDryerSettingsModalProps> = props => {
    const boldTextStyle = {
        fontWeight: "bold"
    };

    const { getPremierPlusBinDtoQueryKey, getPremierPlusBinDtoQuery } = useGetPremierPlusBinDto(props.binDto?.binId ?? 0, { enabled: true, refetchOnWindowFocus: false });
    const initialDryingSettings = useMemo (() => { return getPremierPlusBinDtoQuery.data?.settings?.dryingOperationSettings }, [getPremierPlusBinDtoQuery.dataUpdatedAt]);
    const moistureCalibrationFromSettings = initialDryingSettings?.grainMcCalibration ?? 9;
    const plenumTempFCalibrationFromSettings = initialDryingSettings?.plenumTempCalibrationF ?? 0;

    const uncalibratedGrainTempF = getPremierPlusBinDtoQuery.data?.dryingOperation?.lastUncalibratedSampleTemperatureF;
    const uncalibratedSampleMc = getPremierPlusBinDtoQuery.data?.dryingOperation?.lastUncalibratedSampleMc;
    const snapshotAvgPlenumTemp = useMemo(() => { return getPremierPlusBinDtoQuery.data?.dryingOperation?.averagePlenumTemperatureF }, [getPremierPlusBinDtoQuery.dataUpdatedAt]);
    
    const [form] = Form.useForm<DryingOperationsSettingsDTO>();
    const values = Form.useWatch([], form);

    const [grainTempFCalibration, setGrainTempFCalibration] = useState<number>(0);
    const [moistureCalibration, setMoistureCalibration] = useState<number>(0);
    const [plenumTempFCalibrationFromForm, setPlenumTempFCalibrationFromForm] = useState(0);
    
    const { useCelsius, setUseCelsius } = useCelsiusContext();

    useEffect(() => {
        form.resetFields();
        setGrainTempFCalibration(initialDryingSettings?.grainTempCalibrationF ?? 0);
        setMoistureCalibration(initialDryingSettings?.grainMcCalibration ?? 0);   
        setPlenumTempFCalibrationFromForm(initialDryingSettings?.plenumTempCalibrationF ?? 0);
        form.setFieldsValue(initialDryingSettings ?? ({} as DryingOperationsSettingsDTO));
        form.setFieldValue('timedBypassMinutes', initialDryingSettings?.timedBypassTimeSeconds ?? 0 / 60.0);

        if(getPremierPlusBinDtoQuery.data?.settings?.generalSystemSettings?.useMetricSystem){
            setUseCelsius(true);
            form.setFieldValue('maxPlenumTemperatureF', convertFahrenheitToCelsius(initialDryingSettings?.maxPlenumTemperatureF)!);
            form.setFieldValue('minPlenumTemperatureF', convertFahrenheitToCelsius(initialDryingSettings?.minPlenumTemperatureF)!);
            form.setFieldValue('targetPlenumTemperatureF', convertFahrenheitToCelsius(initialDryingSettings?.targetPlenumTemperatureF)!);
                
            const newGrainTempCalibration = convertFahrenheitToCelsiusOffset(initialDryingSettings?.grainTempCalibrationF);
            const plenumTempCalibration = convertFahrenheitToCelsiusOffset(initialDryingSettings?.plenumTempCalibrationF);
            setGrainTempFCalibration(newGrainTempCalibration!);
            setPlenumTempFCalibrationFromForm(plenumTempCalibration!);
        }else{
            setUseCelsius(false);
            setGrainTempFCalibration(initialDryingSettings?.grainTempCalibrationF ?? 0);
            setPlenumTempFCalibrationFromForm(initialDryingSettings?.plenumTempCalibrationF ?? 0);
        }
    },[initialDryingSettings]);

    const handleChangeUseCelsius = async (val: boolean) => {

        if(getPremierPlusBinDtoQuery.data?.deviceId == null){
            return;
        }
        
        const request : PremierPlusSyncSettingsRequestDTO = {
            azureDeviceId: getPremierPlusBinDtoQuery.data?.deviceId,
            settingsGuid: null,
            isWebRequest: true,
            generalSystemSettings: {
                ...props.binDto?.settings?.generalSystemSettings,
                useMetricSystem: val
            },
            dryingOperationsSettings: null,
            grainMovingSettings: null,
            fanRunSettings: null
        }

        try{
            setIsLoading(true);
            const result = await BinApiService.premierPlus_SetSettings(request);
            setUseCelsius(val);
            queryClient.invalidateQueries({queryKey: [getPremierPlusBinDtoQueryKey]});
            notification.success({ message: 'Successfully changed unit types '});
            if(val){
                form.setFieldValue('maxPlenumTemperatureF', convertFahrenheitToCelsius(values.maxPlenumTemperatureF)!);
                form.setFieldValue('minPlenumTemperatureF', convertFahrenheitToCelsius(values.minPlenumTemperatureF)!);
                form.setFieldValue('targetPlenumTemperatureF', convertFahrenheitToCelsius(values.targetPlenumTemperatureF)!);

                const newGrainTempCalibration = convertFahrenheitToCelsiusOffset(grainTempFCalibration);
                const plenumTempCalibration = convertFahrenheitToCelsiusOffset(plenumTempFCalibrationFromForm);
                setGrainTempFCalibration(newGrainTempCalibration!);
                setPlenumTempFCalibrationFromForm(plenumTempCalibration!);
            }else{
                form.setFieldValue('maxPlenumTemperatureF', convertCelsiusToFahrenheit(values.maxPlenumTemperatureF)!);
                form.setFieldValue('minPlenumTemperatureF', convertCelsiusToFahrenheit(values.minPlenumTemperatureF)!);
                form.setFieldValue('targetPlenumTemperatureF', convertCelsiusToFahrenheit(values.targetPlenumTemperatureF)!);

                const newGrainTempCalibration = convertCelsiusToFahrenheitOffset(grainTempFCalibration);
                const plenumTempCalibration = convertCelsiusToFahrenheitOffset(plenumTempFCalibrationFromForm);
                setGrainTempFCalibration(newGrainTempCalibration!);
                setPlenumTempFCalibrationFromForm(plenumTempCalibration!);
            }
        }catch(err){
            console.error(err);
            notification.error({ message: 'Could not change unit types' });
            return;
        }finally{
            setIsLoading(false);
        }
    }

    const grainTempCalibrationFromSettings = initialDryingSettings?.grainTempCalibrationF;

    const calibratedGrainTemperatureFPreview = addToPossiblyMissingValue(uncalibratedGrainTempF, grainTempFCalibration);

    const uncalibratedPlenumTemperatureF = substractMaybe(snapshotAvgPlenumTemp, plenumTempFCalibrationFromSettings);
    const calibratedPlenumTemperatureFPreview = addToPossiblyMissingValue(uncalibratedPlenumTemperatureF, plenumTempFCalibrationFromForm);

    const temperatureAdjustmentIncrementF = 1;

    const uncalibratedMoisture = uncalibratedSampleMc;
    const calibratedMoisturePreview = addToPossiblyMissingValue(uncalibratedMoisture, moistureCalibration);

    const grainOptions = getEnumOptions(GrainType);
    const sweepOffTimeOptions = generateSweepOffTimeOptions();

    const handleResetBypassClicked = () => {
        form.setFieldValue("timedBypassMinutes", 0.5);
        form.setFieldValue("timedBypassTimeSeconds", 30);
    };

    const handleIncreaseGrainTemp = () => {
        setGrainTempFCalibration(parseFloat((grainTempFCalibration + temperatureAdjustmentIncrementF).toFixed(2)));
    }

    const handleDecreaseGrainTemp = () => {
        setGrainTempFCalibration(parseFloat((grainTempFCalibration - temperatureAdjustmentIncrementF).toFixed(2)));
    }

    const handleIncreaseMoisture = () => {

        setMoistureCalibration(parseFloat((moistureCalibration + 1).toFixed(2)));
    }

    const handleDecreaseMoisture = () => {
        setMoistureCalibration( parseFloat( (moistureCalibration - 1).toFixed(2) ) );
    }

    const handleIncreasePlenumTemp = () => {
        setPlenumTempFCalibrationFromForm(prev => prev + temperatureAdjustmentIncrementF);
    };

    const handleDecreasePlenumTemp = () => {
        setPlenumTempFCalibrationFromForm(prev => prev - temperatureAdjustmentIncrementF);
    };

    const handleClose = () => {
        props.onClose(null);
    };

    const handleTimedBypassChanged = (seconds: number | null) => {
        form.setFieldValue("timedBypassTimeSeconds", (seconds ?? 0) / 60.0);
    };

    function generateSweepOffTimeOptions(): { label: string; value: SweepOffTime }[] {
        return [
            {
                label: "5",
                value: SweepOffTime.Five
            },
            {
                label: "10",
                value: SweepOffTime.Ten
            },
            {
                label: "15",
                value: SweepOffTime.Fifteen
            },
            {
                label: "20",
                value: SweepOffTime.Twenty
            },
            {
                label: "25",
                value: SweepOffTime.TwentyFive
            },
            {
                label: "30",
                value: SweepOffTime.Thirty
            },
            {
                label: "45",
                value: SweepOffTime.FourtyFive
            },
            {
                label: "60",
                value: SweepOffTime.Sixty
            },
            {
                label: "(10 - 60) Auto",
                value: SweepOffTime.TenToSixty
            },
            {
                label: "(20 - 60) Auto",
                value: SweepOffTime.TwentyToSixty
            },
            {
                label: "(30 - 60) Auto",
                value: SweepOffTime.ThirtyToSixty
            }
        ];
    }

    const [isLoading, setIsLoading] = useState(false);

    const onFinish = async (data: DryingOperationsSettingsDTO) => {
        try{
            if(getPremierPlusBinDtoQuery.data?.deviceId == null){
                return;
            }

            if(useCelsius){
                data.maxPlenumTemperatureF = convertCelsiusToFahrenheit(values.maxPlenumTemperatureF)!;
                data.minPlenumTemperatureF = convertCelsiusToFahrenheit(values.minPlenumTemperatureF)!;
                data.targetPlenumTemperatureF = convertCelsiusToFahrenheit(values.targetPlenumTemperatureF)!;
            }

            data.plenumTempCalibrationF = temperatureAdjustmentIncrementF;
            data.grainTempCalibrationF = grainTempFCalibration;
            data.grainMcCalibration = moistureCalibration;

            const combined: DryingOperationsSettingsDTO = {
                ...(initialDryingSettings ?? {}),
                ...data,
                grainMcCalibration: moistureCalibration,
                grainTempCalibrationF : useCelsius ? convertCelsiusToFahrenheitOffset(grainTempFCalibration)! : grainTempFCalibration,
                plenumTempCalibrationF: useCelsius ? convertCelsiusToFahrenheitOffset(plenumTempFCalibrationFromForm)! : plenumTempFCalibrationFromForm,
            }

            if(data.shutOffWithLgso === false){
                data.lgsoMode = initialDryingSettings?.lgsoMode ?? LowGrainShutOffMode.Auto;
            }

            setIsLoading(true);
            const request : PremierPlusSyncSettingsRequestDTO = {
                azureDeviceId: getPremierPlusBinDtoQuery.data?.deviceId,
                settingsGuid: null,
                isWebRequest: true,
                generalSystemSettings: {
                    ...getPremierPlusBinDtoQuery.data?.settings?.generalSystemSettings,
                    useMetricSystem: useCelsius
                },
                dryingOperationsSettings: combined,
                grainMovingSettings: null,
                fanRunSettings: null
            }

            const result = await BinApiService.premierPlus_SetSettings(request);
            notification.success({ message: 'Settings successfully set'});
            props.onClose(null);

        }catch(err){
            notification.error({message: 'Could not set drying settings'});
        }finally{
            setIsLoading(false);
        }
    };

    return (
        <Modal 
        {...props} 
        title={<p>Auger Settings</p>} 
        destroyOnClose 
        footer={null}
        width="100%" 
        style={{ maxWidth: 800 }} 
        >
            <Spin spinning={isLoading}>
                <Form form={form} onFinish={onFinish} initialValues={initialDryingSettings ?? ({} as DryingOperationsSettingsDTO)}>
                    <Row justify={"start"}>
                        <Title style={{ margin: "0px", paddingLeft: "10px", paddingTop: "10px" }} level={3}>
                            Adjust Dryer Settings 
                        </Title>
                    </Row>
                    <br/>
                    <Row gutter={10} justify={'center'} style={{ margin: "5px" }}>
                        <Col span={8}>
                            <Form.Item 
                            name={"maxPlenumTemperatureF"} 
                            label={"Max Temp"}>
                                <InputNumber 
                                inputMode="decimal" 
                                controls={false} 
                                suffix={ useCelsius ? '°C' : '°F' } 
                                style={{ width: '100%' }} 
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={"minutesToFirstSample"} label={"Min to samp:"}>
                                <InputNumber controls={false} inputMode="decimal" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={"samplesCntInAverage"} label={"Num samp in avg:"}>
                                <InputNumber controls={false} inputMode="decimal" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <br/>
                    <Row gutter={10} style={{ margin: '5px' }}>
                        <Col span={8}>
                            <Form.Item  
                            name={"minPlenumTemperatureF"}
                            label="Min Temp:"
                            className="form-item">
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                suffix={ useCelsius ? '°C' : '°F'  }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item 
                            name={"fanShutOffDelayMinutes"}
                            className="form-item"
                            label={"Fan Shut Min:"}
                            >
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item 
                            name={"targetMc"}
                            className="form-item"
                            label={"Transfer Moist:"}>
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                suffix="%" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <br/>
                    <Row gutter={10} style={{ margin:"5px" }}>
                        <Col span={8}>
                            <Form.Item 
                            name={"targetPlenumTemperatureF"}
                            className="form-item"
                            label="Starting target Temp:"
                            >
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                suffix={useCelsius ? '°C' : '°F'  }
                                />
                                </Form.Item>
                        </Col>
                    </Row>
                    <br/>
                    {
                        (
                            <>
                                <Row justify={"start"} gutter={8} style={{ paddingLeft: "10px" }}>
                                    <Col>
                                        <Form.Item
                                            label={<Text>LGSO Mode</Text>}
                                            required={false}
                                            style={{ margin: 0, padding: 0 }}
                                            name={"lgsoMode"}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: ""
                                                }
                                            ]}
                                        >
                                            <Radio.Group buttonStyle={"solid"} defaultValue={false} optionType="button">
                                                <Radio value={LowGrainShutOffMode.Auto}>
                                                    AUTO
                                                </Radio>
                                                <Radio value={LowGrainShutOffMode.TimedBypass}>
                                                    TIMED BYPASS
                                                </Radio>
                                                <Radio value={LowGrainShutOffMode.Bypass}>
                                                    BYPASS
                                                </Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>
                                    {values?.lgsoMode === LowGrainShutOffMode.TimedBypass && (
                                        <Col>
                                            <Row gutter={8}>
                                                <Col>
                                                    <Form.Item
                                                        style={{ margin: 0, padding: 0 }}
                                                        name={"timedBypassMinutes"}
                                                        required={false}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: ""
                                                            }
                                                        ]}
                                                        label={<Text>Timed Bypass minutes</Text>}
                                                    >
                                                        <InputNumber
                                                            style={{ width: "75px" }}
                                                            controls={false}
                                                            onChange={handleTimedBypassChanged}
                                                            inputMode="decimal"
                                                            placeholder="Minutes..."
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col>
                                                    <Button onClick={handleResetBypassClicked}>RESET</Button>
                                                </Col>
                                            </Row>
                                            <Row justify={"center"}>
                                                <Text style={{ fontSize: "12px" }} strong>
                                                    Timed Bypass only counts down while the machine is on
                                                </Text>
                                            </Row>
                                        </Col>
                                    )}
                                </Row>
                                <br/>
                            </>
                        )
                    }
                        <Row style={{ paddingLeft:'10px'}}>
                            <Col span={13}>
                                <Form.Item
                                name={"disableMoistureChangingCheck"}
                                label={<Text>Disable Check for Moisture Not Changing</Text>}
                                valuePropName="checked"
                                style={{ margin:0, padding:0 }}
                                >
                                    <Radio.Group
                                    buttonStyle={'solid'} 
                                    defaultValue={false} 
                                    optionType="button" 
                                    >
                                        <Radio value={false}>NO</Radio>
                                        <Radio value={true}>YES</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col>
                                <Form.Item
                                label={<Text>Use Celsius</Text>}
                                style={{ margin:0, padding:0 }}
                                >
                                    <Radio.Group
                                    defaultValue={useCelsius}
                                    buttonStyle={'solid'} 
                                    optionType="button" 
                                    onChange={(val) => handleChangeUseCelsius(val.target.value)}
                                    >
                                        <Radio value={false}>NO</Radio>
                                        <Radio value={true}>YES</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row justify={'center'}>
                            <Col span={12}>
                                <Form.Item 
                                labelCol={{ span: 24}}
                                style={{ margin: 0, padding: 0}}
                                label={'Sweep off time adjust'}
                                name={"sweepOffTime"}
                                className="form-item"
                                >
                                    <Select
                                    style={{ width:"250px" }}
                                    dropdownStyle={{ 
                                        borderRadius:'0px',
                                        backgroundColor: 'white' 
                                    }}
                                    >
                                        {sweepOffTimeOptions.map((gt) => (
                                            <Option key={gt.value} value={gt.value}>
                                                <span className="select-fantype-text">{gt.label} min.</span>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>  
                            </Col>
                            <Col>
                                <Form.Item
                                labelCol={{ span: 24}}
                                style={{ margin: 0, padding: 0}}
                                label={'Grain Type'}
                                name={"grainType"}
                                className="form-item"
                                >
                                    <Select
                                    style={{ width:"250px" }}
                                    dropdownStyle={{ 
                                        borderRadius:'0px',
                                        backgroundColor: 'white' 
                                    }}
                                    >
                                        {grainOptions.map((gt) => (
                                            <Option key={gt.value} value={gt.value}>
                                                <span className="select-fantype-text">{getGrainTypeStr(gt.label)}</span>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>    
                            </Col>
                        </Row>

                        <Row justify={'center'}>
                            <Col span={20} style={{ textAlign: 'center' }}>
                                <Text>
                                    Adjust the grain temp cal, and the plenum temp cal by tapping up/down. the calibration will be shown on the right and the adjusted value will be on the left
                                </Text>
                            </Col>
                        </Row>

                    <Row justify='center' gutter={10} style={{ padding: '4px' }}>
                            <Col span={4}>
                                <div>Grain Temp:</div>
                            </Col>
                            <Col span={3}>
                                <div style={{ borderRadius:'9999px', fontFamily: 'monospace'}}>
                                    {
                                        useCelsius ? 
                                        formatNumber(convertFahrenheitToCelsius(calibratedGrainTemperatureFPreview), { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°C'})
                                        :
                                        formatNumber(calibratedGrainTemperatureFPreview, { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°F'})
                                    }
                                </div>
                            </Col>
                            <Col span={6}>
                                <Button onClick={handleDecreaseGrainTemp} disabled={grainTempFCalibration <= -20}>Grain Temp Down</Button>
                            </Col>
                            <Col span={6}>
                                <Button onClick={handleIncreaseGrainTemp} disabled={grainTempFCalibration >= 20}>Grain Temp Up</Button>
                            </Col>
                            <Col span={3}>
                                <div style={{ borderRadius:'9999px', fontFamily: 'monospace'}}>
                                    {
                                        useCelsius ? 
                                        formatNumber(grainTempFCalibration, { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°C'})
                                        :
                                        formatNumber(grainTempFCalibration, { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°F'})
                                    }
                                </div>
                            </Col>
                        </Row>

                        <Row justify='center'  gutter={10} style={{ padding: '4px' }}>
                            <Col span={4}>
                                <div>Moisture: </div>
                            </Col>
                            <Col span={3}>
                                <div style={{ borderRadius:'9999px', fontFamily: 'monospace'}}>{formatNumber(calibratedMoisturePreview, {decimalPlaces: 1})} %</div>
                            </Col>
                            <Col span={6}>
                                <Button onClick={handleDecreaseMoisture} disabled={moistureCalibration <= -8}>Moist Down</Button>
                            </Col>
                            <Col span={6}>
                                <Button onClick={handleIncreaseMoisture} disabled={moistureCalibration >= 8}>Moist Up</Button>
                            </Col>
                            <Col span={3}>
                                <div style={{ borderRadius:'9999px', fontFamily: 'monospace'}}>{moistureCalibration} %</div>
                            </Col>
                        </Row>
                        
                        <Row justify='center' gutter={10} style={{ padding: '4px' }}>
                            <Col span={4}>
                                <div>Plenum Temp:</div>
                            </Col>
                            <Col span={3}>
                                <div style={{ borderRadius:'9999px', fontFamily: 'monospace'}}>
                                    {
                                        useCelsius ? 
                                        formatNumber(convertFahrenheitToCelsius(calibratedPlenumTemperatureFPreview), { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°C' })
                                        :
                                        formatNumber(calibratedPlenumTemperatureFPreview, { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°F' })
                                    }
                                </div>
                            </Col>
                            <Col span={6}>
                                <Button onClick={handleDecreasePlenumTemp} disabled={plenumTempFCalibrationFromForm <= -20}>Plen Temp Down</Button>
                            </Col>
                            <Col span={6}>
                                <Button onClick={handleIncreasePlenumTemp} disabled={plenumTempFCalibrationFromForm >= 20}>Plen Temp Up</Button>
                            </Col>
                            <Col span={3}>
                                <div style={{ borderRadius:'9999px', fontFamily: 'monospace'}}>
                                    {
                                        useCelsius ? 
                                        formatNumber(plenumTempFCalibrationFromForm, { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°C'})
                                        :
                                        formatNumber(plenumTempFCalibrationFromForm, { decimalPlaces: 1, showSuffixIfNoValue: true, suffix: '°F'})
                                    }
                                </div>
                            </Col>
                        </Row>

                    <Row justify={"end"} gutter={12}>
                        <Col>
                            <Button onClick={handleClose}>Cancel</Button>
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button htmlType="submit" type="primary">
                                    Submit
                                </Button>
                            </Form.Item>

                        </Col>
                    </Row>
                </Form>
            </Spin>

        </Modal>
    );
};

export default PremierPlusDryerSettingsModal;
